import Parse from "parse";
import {
  getClassName,
  setLimit,
  addImageIfExists,
  setACL,
  convertToPointer,
  addFileOrNull,
  filterKeys
} from "./ParseConfiguration";
import { Event, EventModel } from "../Models/Event.ts";
import { handleError } from "./ErrorHandler.ts";


const EVENT_TABLE: any = getClassName("Event")


export async function getEvents(): Promise<EventModel[]> {
  try {
    const query = new Parse.Query(EVENT_TABLE)
    query.include("speakers")
    query.include("guests")
    query.descending("startDate")
    const count = await query.count()
    setLimit(query, count)
    const result = await query.find()

    return result.map((parseObject: any) =>
      Event.convertFromParseObject(parseObject)
    )
  } catch (error) {
    handleError(error)
    throw error
  }
}

export async function getEventById(objectId: string): Promise<EventModel> {
  try {
    const query = new Parse.Query(EVENT_TABLE)
    query.include("speakers")
    query.include("members")
    query.include("interestedMembers")
    query.include("guests")
    const parseObject = await query.get(objectId)
    const event = Event.convertFromParseObject(parseObject)
    return event

  } catch (error) {
    handleError(error)
    throw error
  }
}

export async function addOrUpdateEvent(newEvent: EventModel, image?: string, pdfAttachment?: any): Promise<void> {
  try {
    const Event = EVENT_TABLE
    const eventObject = new Event()
    Object.entries(newEvent)
      .filter((key) => !filterKeys.includes(key[0]))
      .forEach((key) => {
        if (key[0] === "capacity") {
          eventObject.set(key[0], Number(newEvent[key[0]]))
        } else {
          eventObject.set(key[0], newEvent[key[0]])
        }
        eventObject.set([key[0], key[1]])
      })
    await addImageIfExists(eventObject, image, "image")
    addFileOrNull(eventObject, pdfAttachment, "pdfAttachment")
    addDefaultFieldsIfNew(eventObject, newEvent.objectId)

    const addedSpeakers = newEvent.speakers?.map((speaker) => convertToPointer("Speaker", speaker.objectId))
    eventObject.set("speakers", addedSpeakers)

    const point = new Parse.GeoPoint(newEvent.location.latitude, newEvent.location.longitude)
    eventObject.set("location", point)

    const addedMembers = newEvent.members?.map((member) => convertToPointer("_User", member.objectId))
    eventObject.set("members", addedMembers)

    const addedInterestedMembers = newEvent.interestedMembers?.map((member) => convertToPointer("_User", member.objectId))
    eventObject.set("interestedMembers", addedInterestedMembers)

    const addedGuests = newEvent.guests?.map((guest) => convertToPointer("Guest", guest.objectId))
    eventObject.set("guests", addedGuests)

    await eventObject.save()

  } catch (error) {
    handleError(error)
    throw error
  }
}

export async function deleteEvent(objectId: string): Promise<void> {
  try {
    const query = new Parse.Query(EVENT_TABLE)
    query.get(objectId)
    const result = await query.find()
    if (result.length >= 1) {
      await result[0].destroy()
    }

  } catch (error) {
    handleError(error)
    throw error
  }
}

export async function eventsForUser(objectId: string): Promise<EventModel[]> {
  try {
    const userObject = convertToPointer("_User", objectId)
    const query = new Parse.Query(EVENT_TABLE)
    query.containsAll("members", [userObject])
    query.descending("startDate")
    includePointers(query)
    const result = await query.find()
    return result.map((event: any) => Event.convertFromParseObject(event))

  } catch (error) {
    handleError(error)
    throw error
  }
}

export async function updateEventIsNewValue(objectId, eventsOrder, isNew) {
  try {
    const query = new Parse.Query(EVENT_TABLE)
    query.get(objectId)
    const result = await query.find()
    if (result.length >= 1) {
      result[0].set("isNew", isNew)
      const newEventsOrder = isNew ? eventsOrder : null
      result[0].set("newMembersOrder", newEventsOrder)
      result[0].save()
    }

  } catch (error) {
    handleError(error)
    throw error
  }
}

export async function updateEventpHidingValue(objectId, isHiding) {
  try {
    const query = new Parse.Query(EVENT_TABLE)
    query.get(objectId)
    const result = await query.find()
    if (result.length >= 1) {
      result[0].set("hidden", isHiding)
      result[0].save()
    }

  } catch (error) {
    handleError(error)
    throw error
  }
}

export function updateQVaultValue(event, boolValue) {
  const Event = EVENT_TABLE
  const object = new Event()
  object.set("objectId", event.objectId);
  object.set("isQVault", boolValue);

  return object.save();
}

export async function updateAttachment(event, attachment) {
  const Event = EVENT_TABLE
  const object = new Event()
  object.set("objectId", event.objectId);
  addFileOrNull(object, attachment, "pdfAttachment");
  return object.save();
}

export function notifyUsersAboutEvent(id, title) {
  const params = { eventId: id, eventTitle: title }
  return Parse.Cloud.run("eventNotification", params)
}

function addDefaultFieldsIfNew(parseObject, objectId) {
  if (objectId === undefined) {
    setACL(parseObject);
  }
}

function includePointers(parseQuery) {
  parseQuery.include("speaker");
  parseQuery.include("members");
}
